/**
 * Merger Analysis API Service
 *
 * Handles API calls for carrier merger analysis functionality.
 */

import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types for Merger Analysis
export interface CarrierSummary {
  dotNumber: number;
  legalName: string;
  dbaName?: string;
  totalDrivers: number;
  totalPowerUnits: number;
  safetyRating?: string;
  basicScores: {
    [key: string]: {
      percentile: number;
      measure?: number;
      threshold: number;
      status: 'OK' | 'WARNING' | 'ALERT';
    };
  };
  totalViolations: number;
  totalInspections: number;
  totalCrashes: number;
  fatalCrashes: number;
}

export interface CombinedBasic {
  measure: number;
  percentile: number;
  threshold: number;
  status: 'OK' | 'WARNING' | 'ALERT';
  change: number;
  previousAvg: number;
}

export interface CombinedBasics {
  'Unsafe Driving': CombinedBasic;
  'HOS Compliance': CombinedBasic;
  'Vehicle Maintenance': CombinedBasic;
  'Controlled Substances': CombinedBasic;
  'Driver Fitness': CombinedBasic;
  'Crash Indicator'?: CombinedBasic;
  'Hazmat Compliance'?: CombinedBasic;
}

export interface RiskFactor {
  category: string;
  impact: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  score: number;
}

export interface RiskAssessment {
  score: number;
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  factors: RiskFactor[];
  complianceIssues: string[];
  recommendation: 'APPROVE' | 'REJECT' | 'REVIEW';
  rationale: string;
}

export interface MergerComparison {
  beforeMerger: {
    avgPercentile: number;
    scoresAboveThreshold: number;
    totalViolations: number;
    totalCrashes: number;
  };
  afterMerger: {
    avgPercentile: number;
    scoresAboveThreshold: number;
    totalViolations: number;
    totalCrashes: number;
  };
  improvement: {
    categories: string[];
    impact: number;
  };
  deterioration: {
    categories: string[];
    impact: number;
  };
}

export interface MergerAnalysis {
  id?: string;
  carriers: CarrierSummary[];
  combined: {
    dotNumbers: number[];
    totalFleet: {
      powerUnits: number;
      drivers: number;
      vehicles: number;
    };
    totalViolations: number;
    totalInspections: number;
    totalCrashes: number;
    fatalCrashes: number;
    combinedBasics: CombinedBasics;
    peerGroup: string;
    utilizationFactor: number;
  };
  comparison: MergerComparison;
  riskAssessment: RiskAssessment;
  calculatedAt: string;
}

export interface MergerAnalysisRequest {
  carriers: number[];
  analysisType: 'acquisition' | 'combination' | 'portfolio';
  useOfficialData?: boolean;
}

export interface MergerAnalysisResponse {
  success: boolean;
  data: {
    mergerAnalysis: MergerAnalysis;
  };
}

export interface MergerHistoryItem {
  id: string;
  carriers: number[];
  carrierNames: string[];
  analysisType: string;
  riskLevel: string;
  recommendation: string;
  createdAt: string;
}

export interface MergerHistoryResponse {
  success: boolean;
  data: {
    history: MergerHistoryItem[];
    total: number;
  };
}

// Merger API functions
export const mergerApi = {
  /**
   * Analyze a merger between multiple carriers
   */
  analyzeMerger: async (
    dotNumbers: number[],
    analysisType: 'acquisition' | 'combination' | 'portfolio' = 'acquisition',
    useOfficialData: boolean = true
  ): Promise<MergerAnalysis> => {
    const response = await api.post<MergerAnalysisResponse>('/mergers/analyze', {
      carriers: dotNumbers,
      analysisType,
      useOfficialData,
    });

    // Handle wrapped response
    if (response.data?.data?.mergerAnalysis) {
      return response.data.data.mergerAnalysis;
    }

    // Type-safe fallback for unwrapped response
    if (typeof response.data === 'object' && response.data !== null) {
      const data = response.data as unknown as Record<string, unknown>;
      if ('mergerAnalysis' in data && typeof data.mergerAnalysis === 'object') {
        return data.mergerAnalysis as MergerAnalysis;
      }
    }

    throw new Error('Invalid response format from merger analysis API');
  },

  /**
   * Get a previously saved merger analysis by ID
   */
  getMergerAnalysis: async (id: string): Promise<MergerAnalysis> => {
    const response = await api.get(`/mergers/${id}`);
    if (response.data?.data?.mergerAnalysis) {
      return response.data.data.mergerAnalysis;
    }
    if (response.data?.mergerAnalysis) {
      return response.data.mergerAnalysis;
    }
    return response.data;
  },

  /**
   * Get user's merger analysis history
   */
  getMergerHistory: async (limit: number = 10, offset: number = 0): Promise<{
    history: MergerHistoryItem[];
    total: number;
  }> => {
    const response = await api.get<MergerHistoryResponse>(
      `/mergers/history?limit=${limit}&offset=${offset}`
    );

    if (response.data?.data) {
      return response.data.data;
    }
    return { history: [], total: 0 };
  },

  /**
   * Export merger analysis as PDF
   */
  exportMergerPdf: async (analysisId: string): Promise<Blob> => {
    const response = await api.get(`/mergers/${analysisId}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Export merger analysis as CSV
   */
  exportMergerCsv: async (analysisId: string): Promise<Blob> => {
    const response = await api.get(`/mergers/${analysisId}/export/csv`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Export merger analysis as JSON
   */
  exportMergerJson: async (analysisId: string): Promise<MergerAnalysis> => {
    const response = await api.get(`/mergers/${analysisId}/export/json`);
    return response.data;
  },

  /**
   * Search carriers for merger selection
   */
  searchCarriers: async (query: string, type: string = 'name'): Promise<any[]> => {
    const response = await api.get(
      `/search/carriers?q=${encodeURIComponent(query)}&type=${type}`
    );
    return response.data?.results || response.data || [];
  },
};

export default mergerApi;
